import React from 'react';
import { Checkbox, FormControlLabel, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import QuantityControls from './QuantityControls';
import { AdditionalService } from '../../../../../types/bookingCreation';
import { useTranslation } from 'react-i18next';
import { getCurrentLang } from '../../../../../i18n/i18n';
import { formatCurrency } from '../../../../../utils';
import CurrentPriceHint from './CurrentPriceHint';

type Props = {
  service: AdditionalService;
  isChecked: boolean;
  quantity: number;
  onToggle: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  isMandatory?: boolean;
  // The service's price in the catalog today. On the edit screen `service`
  // carries the *booked* price for lines already on the booking, so this names
  // the current one beside it when the two differ (#1735).
  currentPrice?: number | null;
};

const ServiceItem: React.FC<Props> = ({ service, isChecked, quantity, onToggle, onIncrement, onDecrement, isMandatory, currentPrice }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    // Quantity is only adjustable when the service is selectable-by-quantity and currently selected.
    const showQuantity = !isMandatory && isChecked && service.quantity;
    return (
        <Box className={'flex items-center h-[30px] gap-10'}>
            <FormControlLabel
                control={
                    <Checkbox
                        sx={{
                            color: theme.palette.text.secondary,
                            '&.Mui-checked': { color: theme.palette.primary.light },
                            pr: '2px',
                        }}
                        checked={isMandatory ? true : isChecked}
                        onChange={onToggle}
                        disabled={isMandatory}
                    />
                }
                label={
                    <Typography className={'text-sm flex items-center text-foreground/70'}>
                        {service.langNames?.[getCurrentLang()] || service.serviceName}
                        {service.unitPrice === 0 ? (
                            <span> | {t('booking_details.free')}</span>
                        ) : (
                            <span>
                                {' '}| {formatCurrency(service.unitPrice, getCurrentLang())}
                                {(service as { priceUnit?: string | null }).priceUnit
                                    ? ` ${t('price_unit.' + (service as { priceUnit?: string | null }).priceUnit)}`
                                    : ''}
                                {service.quantity ? ` ${t('booking_details.per_unit')}` : ''}
                            </span>
                        )}
                        <CurrentPriceHint bookedPrice={service.unitPrice} currentPrice={currentPrice} />
                    </Typography>
                }
                sx={{ marginRight: '-15px' }}
            />
            {showQuantity && (
                <Box className={'flex items-center gap-1.5'}>
                    <span className={'text-xs text-foreground/60'}>{t('booking_details.quantity')}</span>
                    <QuantityControls quantity={quantity} onIncrement={onIncrement} onDecrement={onDecrement} />
                </Box>
            )}
        </Box>
    );
};

export default ServiceItem;
